// Shared types used across extracted components

export interface PracticeLocation {
  label: string
  address: string
  phone: string
  fax: string
}

export interface DoctorProfile {
  id: string
  name: string
  npi: string
  specialty: string
  dea?: string
  practiceName: string
  locations: PracticeLocation[]
  practicePhone?: string
  practiceFax?: string
  practiceAddress?: string
}

export interface UserData {
  email: string
  name: string
  plan: string
  loggedIn: boolean
  lmnCount: number
  trialLmnLimit: number
  trialActive: boolean
  billingStarted: boolean
  isAdmin: boolean
  referralCode: string
  referredBy: string | null
  referralCount: number
  referralPaidCount: number
  freeMonthsEarned: number
  freeMonthsUsed: number
  monthlyLmnCount: number
  monthlyLmnLimit: number
  bonusLmnCount: number
  bonusLmnUsed: number
  currentMonth: string
  createdAt: string
  paymentFailed: boolean
  cancelAtPeriodEnd: boolean
  subscriptionEndsAt: string | null
  currentPeriodEnd: string | null
}

export interface Citation { number: number; source: string; title: string; url?: string }
export interface FillIn   { field: string; placeholder: string; description: string }
export interface LMNResult {
  success: boolean
  letter_text: string
  letter_sections: Record<string, string>
  citations: Citation[]
  provider_fill_ins: FillIn[]
  diagnosis_descriptions: string[]
  denial_rebuttal_strategy: string
  confidence_note?: string
  generation_mode: string
}

export interface UsageAlert {
  type: string
  remaining: number
  limit: number
  plan: string
  message: string
}

export interface PlanResult {
  plan_name: string
  contract_id: string
  plan_id: string
  formulary_id: string
  plan_category: string
  plan_type_detail: string
  segment_count: number
  unverified?: boolean
}

export interface ICD10Result {
  code: string
  description: string
  body_system: string
}

export interface MedicalPolicy {
  found: boolean
  carrier_name?: string
  policy_number?: string
  policy_title?: string
  policy_base_url?: string
  indication?: string
  required_prior_treatments?: string[]
  required_documentation?: string[]
  approval_duration?: string
  quantity_limit?: string
  message?: string
}

export const CARRIERS = [
  'Aetna','BlueCross BlueShield','Cigna','UnitedHealthcare','Humana',
  'Molina Healthcare','Centene / Ambetter','Kaiser Permanente','Elevance Health (Anthem)','CVS Health / Caremark',
  'Highmark','Independence Blue Cross','Regence','Carefirst BlueCross','Florida Blue',
  'WellCare','Bright Health','Oscar Health','Clover Health','Devoted Health',
  'Tricare','Medicaid (State Plans)','Medicare Part D','Optum','Medica',
  'Health Net','GEHA','Priority Health','SelectHealth','Premera Blue Cross',
  'Harvard Pilgrim','Tufts Health Plan','EmblemHealth','HealthFirst','Excellus BCBS',
  'Horizon BCBS NJ','BCBS Michigan','BCBS Texas','BCBS Illinois','BCBS North Carolina',
  'AmeriHealth Caritas','CareSource','Wellpoint / Amerigroup','Fidelis Care','L.A. Care Health Plan',
  'CalOptima','Partnership HealthPlan','UnitedHealthcare Community Plan','Aetna Better Health',
  'Express Scripts','Prime Therapeutics','CalPERS','NYSHIP','FEHB Plans',
]

export const PLAN_TYPES = [
  { value: 'commercial',          label: 'Commercial' },
  { value: 'medicare_advantage',  label: 'Medicare Advantage' },
  { value: 'medicaid_managed',    label: 'Medicaid Managed Care' },
  { value: 'self_insured',        label: 'Self-Insured / ERISA' },
  { value: 'medicare_part_d',     label: 'Medicare Part D' },
  { value: 'union_multiemployer', label: 'Union / Multiemployer' },
  { value: 'state_employee',      label: 'State Employee Plan' },
  { value: 'federal_employee',    label: 'Federal Employee (FEHB)' },
  { value: 'exchange_marketplace',label: 'ACA Marketplace / Exchange' },
  { value: 'workers_comp',        label: "Workers' Compensation" },
  { value: 'tricare',             label: 'TRICARE (Military)' },
]

export const DENIAL_REASONS = [
  { value: 'not_medically_necessary',      label: 'Not Medically Necessary' },
  { value: 'non_formulary',                label: 'Non-Formulary' },
  { value: 'step_therapy_required',        label: 'Step Therapy Required' },
  { value: 'experimental_investigational', label: 'Experimental / Investigational' },
  { value: 'pa_not_obtained',              label: 'Prior Auth Not Obtained' },
  { value: 'quantity_limit_exceeded',      label: 'Quantity / Duration Limit Exceeded' },
]

// Shared UI pieces
export const InputClass = "w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 bg-white transition-all placeholder:text-slate-300 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-slate-400"

export const Badge = ({ children, color }: { children: React.ReactNode; color: string }) => (
  <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${color}`}>{children}</span>
)

export const Label = ({ children }: { children: React.ReactNode }) => (
  <label className="block text-[13px] font-semibold text-slate-700 dark:text-slate-200 mb-1.5">{children}</label>
)

import type { TeamData } from '../api'
export type { TeamData }

import type { NotificationItem, SavedTemplateData, BulkLMNItemRequest, BulkLMNResultItem } from '../api'
export type { NotificationItem, SavedTemplateData, BulkLMNItemRequest, BulkLMNResultItem }

import type { AppealsDashboardData } from '../api'
export type { AppealsDashboardData }
